import CloseIcon from '@mui/icons-material/Close'
import { Drawer } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { ChangeEvent, ReactElement, useEffect, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { I18n, Translate2 } from 'common'
import { zIndex } from '../../../../../src/theme/zIndex'
import { Button } from '../../../../../src/ui-components/forms/Button'
import { ReactSelect } from 'src/ui-components/forms/ReactSelect'
import { TextInput } from '../../../../../src/ui-components/forms/TextInput'
import { Thumbnail } from '../../../../../src/ui-components/products/Thumbnail'
import styled from 'styled-components'
import { create as createNiceModal, useModal } from '@ebay/nice-modal-react'
import LostSaleServiceProvider from 'src/services/LostSaleServiceProvider'
import {
  LostSalesReasons,
  LostSalesReport,
} from '../../../../../src/store/models/LostSalesModel'
import { NonCachedSearchType } from 'interfaces'
import { ProductLocationModel, ProductModel } from 'interfaces'
import { Vehicle } from '../../../../../src/store/models/Vehicles'
import { formatVehicleDisplay } from 'src/helpers/utils'
import ShowNotification from 'src/ui-components/modals/ToastNotifcationNotiStack'
import { ToastNotificationVariant } from 'src/ui-components/util/Interfaces'

enum SearchType {
  navigation = 'YMME',
  interchange = 'INTERCHANGE',
  partType = 'PART-TYPE',
  AllProducts = 'AST',
  partEnq = 'PART-INQUIRY',
}

interface ILostSalesReason {
  id: number
  value: string
}
interface IReportLostSaleDrawer {
  product: ProductModel
  quantity: number
  activeLocation: ProductLocationModel
  lastNonCachedSearch: NonCachedSearchType
  vehicle: Vehicle
}

export const ReportLostSaleDrawer = createNiceModal<IReportLostSaleDrawer>(
  observer((props: IReportLostSaleDrawer): ReactElement => {
    const modal = useModal()
    const { product, quantity, activeLocation, lastNonCachedSearch, vehicle } =
      props
    const [lostSalesReasons, setLostSaleReasons] = useState<LostSalesReasons[]>(
      []
    )
    const [lostSaleReportStatus, setLostSaleReportStatus] = useState(0)

    const thumbnail = product?.partImages?.length
      ? product.partImages.find(
          (img: { type: string }) => img.type === 'thumbnail'
        )?.url
      : ''

    const ref = useRef<HTMLInputElement>(null)

    const [comment, setcomment] = useState('')
    const [otherReason, setOtherReason] = useState('')
    const [selectedReason, setSelectedReason] =
      useState<ILostSalesReason | null>(null)
    const [disbaleSubmitButton, setDisbaleSubmitButton] = useState(true)
    const [submitInProgress, setSubmitInProgress] = useState(false)
    const [isLoadingReasons, setIsLoadingReasons] = useState(true)
    const handleClose = (): void => {
      modal.remove()
    }
    useEffect(() => {
      setIsLoadingReasons(true)
      LostSaleServiceProvider.getLostSalesReasons()
        .then((resp) => {
          setLostSaleReasons([
            ...resp,
            {
              id: 99,
              value: 'other',
            },
          ])
          setSelectedReason(resp[0])
          setDisbaleSubmitButton(false)
        })
        .catch((error) => {
          ShowNotification({
            message: error?.message || Translate2('failedToGetReasons'),
            variant: ToastNotificationVariant.ERROR,
          })
        })
        .finally(() => {
          setIsLoadingReasons(false)
        })
    }, [])

    useEffect(() => {
      if (otherReason === '') {
        setDisbaleSubmitButton(true)
      } else {
        setDisbaleSubmitButton(false)
      }
    }, [otherReason])

    useEffect(() => {
      if (lostSaleReportStatus === 200) {
        ShowNotification({
          message: Translate2('lostSaleSuccess'),
          variant: ToastNotificationVariant.SUCCESS,
        })
        modal.remove()
        setLostSaleReportStatus(0)
      }
    }, [lostSaleReportStatus, setLostSaleReportStatus, modal])
    const handleCommentChange = (event: ChangeEvent) => {
      setcomment((event.target as HTMLInputElement).value)
    }

    const cancelReportHandler = (): void => {
      setDisbaleSubmitButton(false)
      setSelectedReason(null)
      setcomment('')
      handleClose()
    }

    const getLookUpType = () => {
      if (lastNonCachedSearch === NonCachedSearchType.searchByInterchange) {
        return SearchType.interchange
      }
      if (lastNonCachedSearch === NonCachedSearchType.searchByNavigation) {
        return SearchType.navigation
      }
      if (
        lastNonCachedSearch === NonCachedSearchType.searchByMultiPartsInquiry
      ) {
        return SearchType.partEnq
      }
      if (
        lastNonCachedSearch === NonCachedSearchType.searchByText ||
        lastNonCachedSearch === NonCachedSearchType.searchByAstNavigation
      ) {
        return SearchType.AllProducts
      }
      if (lastNonCachedSearch === NonCachedSearchType.searchBySpecificParts) {
        return SearchType.partType
      }
      return SearchType.navigation
    }

    // Lookup type for YMME, VIN, License plate is same in lostsales request data
    const submitLostSaleReport = (): void => {
      setSubmitInProgress(true)
      const lookupType: SearchType = getLookUpType()

      const vehicleStr =
        lookupType === SearchType.navigation
          ? formatVehicleDisplay(vehicle)
          : ''

      const lostSalesReqData: LostSalesReport = {
        lineCode: product.lineCode,
        partNumber: product.partNumber,
        description: product.description,
        brandId: 0,
        brandName: product.brandName || '',
        vehicle: vehicleStr,
        costPrice: activeLocation?.cost,
        listPrice: activeLocation?.list,
        lookupType,
        qty: quantity,
        reasonId: selectedReason?.id ?? 0,
        comment: comment.length > 0 ? comment : '',
        otherReason,
      }
      LostSaleServiceProvider.sendLostSaleReport(lostSalesReqData)
        .then((response) => {
          setLostSaleReportStatus(response)
        })
        .catch((error) => {
          ShowNotification({
            message: error.message || Translate2('failedToSendReport'),
            variant: ToastNotificationVariant.ERROR,
          })
        })
        .finally(() => {
          setSubmitInProgress(false)
        })
    }
    return (
      <Drawer anchor="right" open={modal.visible} onClose={handleClose}>
        <Container ref={ref}>
          <HeaderWrapper>
            <Thumbnail
              urlOrBase64={thumbnail}
              width={100}
              height={100}
              placeholder
            />
            <InfoWrapper>
              <StyledText bold fontSize="28px" applyFontFamily>
                {product.description}
              </StyledText>
              <span>
                <I18n name="partNum" />
                :&nbsp;
              </span>
              <b>{product.partNumber}</b>
              <StyledSpan> &#124;</StyledSpan>
              <span>
                <I18n name="brand" />
                :&nbsp;
              </span>
              <b>{product.brandName ?? product.manufacturerName}</b>
            </InfoWrapper>
            <StyledCloseIcon onClick={handleClose} />
          </HeaderWrapper>
          <StyledText bold fontSize="25px" marginTop="15px" applyFontFamily>
            <I18n name="reportLostSale" />
          </StyledText>
          <RowWrapper>
            {isLoadingReasons ? (
              <Spinner animation="border" />
            ) : (
              <ColumnWrapper>
                <I18n
                  name="reason"
                  style={{
                    flex: 1,
                    display: 'flex',
                    fontWeight: 'bold',
                  }}
                />
                <ReactSelect
                  value={selectedReason ?? { id: 0, value: '' }}
                  options={lostSalesReasons}
                  defaultValue={
                    lostSalesReasons.length > 0 ? lostSalesReasons[0].value : ''
                  }
                  getOptionLabel={(option: unknown) =>
                    Translate2((option as LostSalesReasons).value)
                  }
                  getOptionValue={(option: unknown) =>
                    (option as LostSalesReasons).id.toString()
                  }
                  onChange={(itemSelected) => {
                    const selection = itemSelected as LostSalesReasons
                    setSelectedReason(selection)
                    if (selection.id === 99) {
                      setOtherReason('')
                      setDisbaleSubmitButton(true)
                    } else {
                      setDisbaleSubmitButton(false)
                    }
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      width: '220px',
                    }),
                    option: (base) => ({
                      ...base,
                      fontSize: '16px',
                      width: '220px',
                      minWidth: '220px',
                    }),
                    menu: (base) => ({
                      ...base,
                      width: '220px',
                    }),
                  }}
                />
              </ColumnWrapper>
            )}
            {selectedReason && selectedReason.id === 99 && (
              <ColumnWrapper>
                <I18n
                  name="specify"
                  style={{
                    flex: 1,
                    display: 'flex',
                    fontWeight: 'bold',
                  }}
                />
                <TextInput
                  value={otherReason}
                  onChange={(value) => setOtherReason(value)}
                  noMargin
                  style={{ width: '100%', flex: 1 }}
                />
              </ColumnWrapper>
            )}
          </RowWrapper>

          <StyledText bold marginTop="25px">
            <I18n name="commentOptional" />
          </StyledText>
          <TextArea value={comment} onChange={handleCommentChange} />
          <ButtonsWrapper>
            <Button
              onClick={cancelReportHandler}
              variant="secondary"
              size="medium"
              text={Translate2('cancel')}
            />

            <Button
              onClick={submitLostSaleReport}
              variant="primary"
              size="medium"
              text={Translate2('submit')}
              loading={submitInProgress}
              disabled={disbaleSubmitButton || submitInProgress}
            />
          </ButtonsWrapper>
        </Container>
      </Drawer>
    )
  })
)

const Container = styled.div`
  height: 100%;
  width: 533px;
  background-color: #ffffff;
  z-index: ${zIndex.modal};
  padding: 2rem;
  overflow-y: auto;
`

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
  position: absolute;
  right: 0;
  margin-right: 20px;
`
const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: 'row';
  border-bottom: 1px solid silver;
`

const RowWrapper = styled.div`
  flex-direction: 'row';
  display: flex;
  margin-top: 20px;
`
const InfoWrapper = styled.div`
  flex-direction: 'column';
  width: '100%';
  margin-left: 10px;
  margin-right: 20px;
  margin-bottom: 10px;
  flex: 1;
`

const ColumnWrapper = styled.div`
  flex-direction: 'column';
  width: '100%';
  flex: 1;
`
const StyledSpan = styled.span`
  margin-left: 5px;
  margin-right: 10px;
  text-align: center;
`

const TextArea = styled.textarea`
  width: 100%;
  height: 200px;
  resize: none;
`

interface StyledTextProps {
  applyFontFamily?: boolean
  bold?: boolean
  fontSize?: string
  marginTop?: string
}
const StyledText = styled.div<StyledTextProps>`
  font-family: ${(p) => (p.applyFontFamily ? 'Oswald' : '')};
  font-style: normal;
  font-weight: ${(p) => (p.bold ? 'bold' : 'normal')};
  font-size: ${(p) => (p.fontSize ? p.fontSize : '16px')};
  margin-top: ${(p) => (p.marginTop ? p.marginTop : '0px')};
  width: '100%';
`
const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 20px;
`
